import type { TFunction } from '@jq4race/i18n';

const KEY_PREFIX = 'breadcrumb.';

/**
 * Route `staticData.breadcrumb` segments may be either an i18n key
 * (`breadcrumb.settings`) or the legacy English label ("SMS Templates").
 * This derives the `app-shell` key for a legacy label:
 * "SMS Templates" -> "breadcrumb.smsTemplates", "Check-in Fields" -> "breadcrumb.checkInFields".
 */
export function breadcrumbKey(segment: string): string {
  const words = segment
    .trim()
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  const camel = words
    .map((w, i) => {
      const lower = w.toLowerCase();
      return i === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
  return `${KEY_PREFIX}${camel}`;
}

/** "breadcrumb.stationDevices" -> "Station devices" (used when a key has no translation). */
function humanizeBreadcrumbKey(key: string): string {
  const words = key
    .slice(KEY_PREFIX.length)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Translates one breadcrumb segment. With no translation, a legacy label falls back to its raw
 * text and an i18n key falls back to a humanized form of the key (never the raw key).
 */
export function translateBreadcrumbSegment(t: TFunction, segment: string): string {
  const isKey = segment.startsWith(KEY_PREFIX);
  const key = isKey ? segment : breadcrumbKey(segment);
  return t(key, { defaultValue: isKey ? humanizeBreadcrumbKey(key) : segment });
}
